/**
 * The stand-in a caller holds between `init()` and the recorder actually
 * arriving. Shared by the bundled stub and the CDN loader so both halves make
 * the same promises about that window.
 */

import type { LoaderGlobal } from './globals';

/**
 * Methods that return a plain value rather than a promise. Before the recorder
 * lands there is no value to give, and handing back a promise would be worse
 * than nothing: `if (tracker.hasUnredactedFields())` would be true forever.
 * Naming convention rather than a hardcoded list of ~50 names, which would
 * drift silently every time the tracker gains a getter.
 */
export function isSyncAccessor(method: string): boolean {
    return /^(get|has|is)[A-Z]/.test(method);
}

/**
 * Reading any other property before the recorder exists yields a queuing
 * function, which is right for methods and wrong for these two.
 *
 * `then` matters most: a `then` that looks callable makes the handle a
 * thenable, so `await`-ing it anywhere - or passing it through any promise -
 * hangs or unwraps into the wrong value.
 *
 * `initializationPromise` is public API that callers wait on before calling
 * `start()`, so it has to be a real promise. Handing back the loader's own
 * readiness promise makes that wait mean the same thing it means on a bundled
 * install: the tracker is live and safe to use.
 */
function preloadProperty(g: LoaderGlobal, prop: string): { value: unknown } | null {
    if (prop === 'then' || prop === 'catch' || prop === 'finally') return { value: undefined };
    if (prop === 'initializationPromise') return { value: g.ready };
    return null;
}

export function createTrackerProxy(g: LoaderGlobal): Record<string, any> {
    return new Proxy(
        {},
        {
            get(_target, prop) {
                if (typeof prop !== 'string') return undefined;

                // Once the recorder is up, get out of the way entirely: hand
                // back the real bound method so sync accessors and identity
                // checks behave exactly as they do in a bundled install.
                const real = g.real;
                if (real) {
                    const value = real[prop];
                    return typeof value === 'function' ? value.bind(real) : value;
                }

                const special = preloadProperty(g, prop);
                if (special) return special.value;

                return (...args: unknown[]) => {
                    if (isSyncAccessor(prop)) return undefined;
                    if (g.failed) return Promise.resolve(undefined);
                    return new Promise((res, rej) => {
                        g.q.push({ m: prop, a: args, res, rej });
                    });
                };
            },
        }
    );
}

/** A tracker that will never exist: server render, or a blocked recorder. */
export function createInertProxy(): Record<string, any> {
    return new Proxy(
        {},
        {
            get(_t, prop) {
                if (typeof prop !== 'string') return undefined;
                if (prop === 'then' || prop === 'catch' || prop === 'finally') return undefined;
                if (prop === 'initializationPromise') return Promise.resolve();
                if (isSyncAccessor(prop)) return undefined;
                return () => Promise.resolve(undefined);
            },
        }
    );
}

/**
 * Settle everything queued so awaited calls do not hang for the life of the
 * page. A blocked or failed recorder must never surface in the host page.
 */
export function abandonQueue(g: LoaderGlobal): void {
    g.failed = true;
    const pending = g.q.splice(0, g.q.length);
    for (const call of pending) call.res(undefined);
    // Silence is the worst outcome for whoever is integrating us: nothing
    // recorded and nothing said. One line, once, and never an exception.
    // eslint-disable-next-line no-console
    console.warn(
        '[HumanBehavior] recorder could not be loaded, so nothing will be recorded. ' +
            'This is usually a blocked request (ad blocker, content blocker) or a ' +
            'Content-Security-Policy that does not allow the CDN.'
    );
    // Optional call: this runs inside the CDN loader too, where the global may
    // have been created by an older stub that predates `settle`.
    g.settle?.();
}
